import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { RootState } from "setup";
import { IDataImportService, SubmissionStatus, TemplateType } from "services/DataImportService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { Step } from "../../../shared/submissionModel";
import { writeErrorMessage } from "../../../shared/submissionErrors";
import { clearActiveSubmission, selectActiveSubmission } from "../../ActiveSubmissionSlice";
import { getReferenceData } from "../../ReferenceDataSlice";
import { latestSubmittedStaticData } from "../../../landingPage/SubmissionsListSlice";
import {
  createSubmission,
  getActiveSubmission,
  getSubmissions,
  updateSubmissionStep,
} from "../../../shared/submissionThunks";
import { canonicalWorksheetName, resolveSheetSources, toSheetMapping } from "../../sheetMappings";
import { selectResolvedRowIdentifierFields } from "../Mapping/MappingSlice";
import { buildBundleColumnNames, resolveMappingRows } from "../Mapping/columnMappingRows";
import { STATIC_DATA_FIELDS } from "../Mapping/staticDataFields";
import { externalKeySetWorksheets, STATIC_DATA_KEY_SETS } from "../../templateConfig/externalKeySets";
import { getExternalKeySets, setExternalKeySets } from "./stores/externalKeySetStore";
import { clearUpload, setUploadFiles, uploadSubmissionFiles } from "../FileUpload/FileUploadSlice";
import { clearParsedColumnSources } from "../Parsing/ParsingSlice";
import { getOrDownloadSubmissionFile, getSubmissionFile } from "../FileUpload/submissionFileStore";
import {
  EXCEL_EXTENSIONS,
  getFileExtension,
  MAX_UPLOAD_BYTES,
  suggestWorksheet,
  UploadFileStatus,
  UploadMode,
} from "../FileUpload/uploadRules";
import { readWorkbookSheetNames } from "../FileUpload/workbookReader";
import { worksheetsForTemplate } from "../../../shared/templateData";
import {
  clearCellEdits,
  getCellEditCount,
  getCellEdits,
  hydrateCellEdits,
  recordCellEdit,
} from "./stores/cellEditsStore";
import { buildEditEntries, EDITS_PAYLOAD_VERSION, toCellEditRecords } from "./stores/cellEditsPayload";
import { extractReferenceLists } from "../../templateConfig/referenceListExtractors";
import { toTemplateKind } from "../../templateConfig/validationRuleTypes";
import { findMappedColumnIndex } from "./engine/applyMapping";
import { ValidationRunSummary } from "./engine/validationTypes";
import { runExternalKeySetsInWorker, runValidationInWorker } from "./worker/workerClient";
import { ValidationWorkerFileInput } from "./worker/workerProtocol";
import {
  buildValidationRunData,
  clearValidationRun,
  getValidationRun,
  setValidationRun,
} from "./stores/validationResultStore";
import {
  buildValidationResultPayload,
  parseValidationResultPayload,
  VALIDATION_ENGINE_VERSION,
} from "./stores/validationPayload";

export type ValidationProgress = {
  phase: "reading" | "mapping" | "validating";
  worksheet?: string;
  percent: number;
};

export type ExternalKeySetError = "noStaticData" | "bundleUnavailable" | "failed";
export type ExternalKeySetStatus = "idle" | "loading" | "done" | "failed";
export type ValidationStatus = "idle" | "running" | "done" | "failed";

// "noWorksheets" has its own recovery path (back to Upload).
export type ValidationFailure = "noWorksheets" | "generic";

// User-facing: the global error modal shows the thrown message as-is.
const NO_WORKSHEETS_MESSAGE = "None of your sheets are linked to a template worksheet";

export type StaticDataReference = {
  parentSubmissionID: string;
  parentReferenceNo?: string;
  missingWorksheets: string[];
};

type ValidationState = {
  // Redux holds only what drives rendering decisions. The bulk run output (mapped rows,
  // issues, indexes) lives in validationResultStore keyed by submissionID; components use
  // RunID to re-read it after a run completes.
  Status: ValidationStatus;
  Failure: ValidationFailure | null;
  Progress: ValidationProgress | null;
  Summary: ValidationRunSummary | null;
  RunID: number;
  // Balance Import only; stays "idle" for Static Data.
  ExternalKeySetStatus: ExternalKeySetStatus;
  ExternalKeySetError: ExternalKeySetError | null;
  StaticDataReference: StaticDataReference | null;
  // Inline edits are journaled locally and only pushed to SaveEdits by "Save progress" (or
  // when continuing to review) — Dirty means the journal differs from the server copy.
  EditsDirty: boolean;
  SavingEdits: boolean;
  // Whether the current run's results are checkpointed server-side; "Save progress" covers the gaps.
  ResultPersisted: boolean;
  PersistingResult: boolean;
  // ISO time the displayed Summary was produced.
  ValidatedAt: string | null;
};

const initialState: ValidationState = {
  Status: "idle",
  Failure: null,
  Progress: null,
  Summary: null,
  RunID: 0,
  ExternalKeySetStatus: "idle",
  ExternalKeySetError: null,
  StaticDataReference: null,
  EditsDirty: false,
  SavingEdits: false,
  ResultPersisted: false,
  PersistingResult: false,
  ValidatedAt: null,
};

let nextRunID = 1;

// First completed run moves Mapped → InValidation ("the customer has seen validation results").
export const validationStepStatus = (status: SubmissionStatus) =>
  status === SubmissionStatus.Mapped ? SubmissionStatus.InValidation : status;

// Checkpoints the run (summary + issues) so a resumed submission can show its last results
// immediately. Never rejects — a missing checkpoint is a warning, and the re-run rebuilds it.
export const persistValidationResult = createAsyncThunk(
  "dataImportValidation/persist",
  async (
    arg: { submissionID: string; summary: ValidationRunSummary },
    { getState }
  ): Promise<{ persisted: boolean }> => {
    const state = getState() as RootState;
    const submission = selectActiveSubmission(state);
    const uploadState = state.dataImportUpload.UploadState;
    // The user has moved on to another submission — nothing to checkpoint, nothing to report.
    if (!submission || submission.submissionID !== arg.submissionID || !uploadState) return { persisted: false };

    try {
      const run = getValidationRun(arg.submissionID);
      const payload = buildValidationResultPayload(
        toTemplateKind(submission.templateType),
        arg.summary,
        uploadState.files.map((entry) => ({ submissionFileID: entry.submissionFileID, fileName: entry.fileName })),
        run?.issues
      );
      const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
      // BE requires a JSON document (not a string) and a positive resultVersion.
      await dataImportService.saveValidationResult(arg.submissionID, {
        resultVersion: payload.version,
        totalRows: arg.summary.totalRows,
        validRows: arg.summary.validRows,
        errorRows: arg.summary.errorRows,
        errorCount: arg.summary.errorCount,
        validationResult: payload,
      });
      return { persisted: true };
    } catch (error) {
      toast.warning(
        writeErrorMessage(
          error,
          "Couldn't save your validation results for later — validation will re-run when you come back."
        )
      );
      return { persisted: false };
    }
  }
);

// Resume display: loads the saved checkpoint as an issues-only run until the fresh run rebuilds
// the rows. Any miss resolves to null.
export const hydrateValidationCheckpoint = createAsyncThunk(
  "dataImportValidation/hydrateCheckpoint",
  async (arg: {
    submissionID: string;
  }): Promise<{ summary: ValidationRunSummary; runID: number; validatedAt: string } | null> => {
    try {
      const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
      const model = await dataImportService.getValidationResult(arg.submissionID);
      if (!model?.validationResult) return null;
      const payload = parseValidationResultPayload(model.validationResult);
      if (payload.engineVersion !== VALIDATION_ENGINE_VERSION) return null;
      if (getValidationRun(arg.submissionID)) return null;
      if (!payload.issues) return { summary: payload.summary, runID: 0, validatedAt: payload.validatedAt };
      const runID = nextRunID++;
      setValidationRun(
        arg.submissionID,
        buildValidationRunData(runID, [], [], payload.issues, { fromCheckpoint: true })
      );
      return { summary: payload.summary, runID, validatedAt: payload.validatedAt };
    } catch {
      return null;
    }
  }
);

// Balance Import: extracts key sets from the parent Static Data bundle into externalKeySetStore.
export const loadExternalKeySets = createAsyncThunk<
  StaticDataReference,
  { submissionID: string },
  { rejectValue: ExternalKeySetError }
>("dataImportValidation/loadExternalKeySets", async (arg, { getState, dispatch, rejectWithValue }) => {
  const state = getState() as RootState;
  const submission = selectActiveSubmission(state);
  if (!submission || submission.submissionID !== arg.submissionID) return rejectWithValue("failed");

  // Submissions created before the parent link existed fall back to the latest submitted Static Data.
  let parentSubmissionID = submission.parentSubmissionID;
  let parentReferenceNo = submission.parentReferenceNo;
  if (!parentSubmissionID) {
    let submissions = state.dataImportSubmissionsList.Submissions;
    if (submissions.length === 0) {
      const loaded = await dispatch(getSubmissions());
      submissions = getSubmissions.fulfilled.match(loaded) ? loaded.payload : [];
    }
    const latest = latestSubmittedStaticData(submissions);
    if (!latest) return rejectWithValue("noStaticData");
    parentSubmissionID = latest.submissionID;
    parentReferenceNo = latest.referenceNo;
  }

  const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
  const staticWorksheets = worksheetsForTemplate(TemplateType.StaticData);
  const neededWorksheets = new Set(externalKeySetWorksheets());

  let files: Array<{ worksheet: string; file: File }>;
  try {
    const bundle = await dataImportService.downloadSubmissionBundle(parentSubmissionID);
    const zip = await JSZip.loadAsync(bundle.data);
    files = [];
    for (const entry of Object.values(zip.files)) {
      if (entry.dir) continue;
      const baseName = entry.name.replace(/^.*\//, "").replace(/\.csv$/i, "");
      const worksheet = canonicalWorksheetName(baseName, staticWorksheets);
      if (!worksheet || !neededWorksheets.has(worksheet)) continue;
      files.push({ worksheet, file: new File([await entry.async("blob")], `${worksheet}.csv`) });
    }
  } catch {
    return rejectWithValue("bundleUnavailable");
  }
  // A bundle with none of the referenced worksheets can't be the FE-built Static Data zip.
  if (files.length === 0) return rejectWithValue("bundleUnavailable");

  let columnNamesByField = {};
  try {
    const [rows, sheetMappingModels] = await Promise.all([
      dataImportService.getColumnMappings(parentSubmissionID),
      dataImportService.getSheetMappings(parentSubmissionID),
    ]);
    const sheetMappings = sheetMappingModels.map(toSheetMapping);
    const mapping = resolveMappingRows(rows, sheetMappings, staticWorksheets)?.resolved.mapping ?? {};
    columnNamesByField = buildBundleColumnNames(mapping, sheetMappings, staticWorksheets);
  } catch {
    // Without the parent mapping the worker auto-matches the CSV headers.
  }

  try {
    const fieldNames = Object.fromEntries(
      files.map((entry) => [entry.worksheet, (STATIC_DATA_FIELDS[entry.worksheet] ?? []).map((field) => field.name)])
    );
    const done = await runExternalKeySetsInWorker({
      files,
      columnNamesByField,
      fieldNames,
      keySets: STATIC_DATA_KEY_SETS,
    });
    setExternalKeySets(arg.submissionID, {
      parentSubmissionID,
      parentReferenceNo,
      keySets: done.keySets,
      missingWorksheets: done.missingWorksheets,
    });
    return { parentSubmissionID, parentReferenceNo, missingWorksheets: done.missingWorksheets };
  } catch {
    return rejectWithValue("failed");
  }
});

// Runs the full validation pass for the active submission: gathers the raw File handles
// (re-downloading on a resumed session), ships them to the worker with the committed mapping
// and reference lists, and stores the bulk output in validationResultStore. Failure toasts
// live here, not in reducers.
export const runSubmissionValidation = createAsyncThunk(
  "dataImportValidation/run",
  async (arg: { submissionID: string; refresh?: boolean; skipCheckpoint?: boolean }, { dispatch, getState }) => {
    try {
      const submission = selectActiveSubmission(getState() as RootState);
      if (!submission || submission.submissionID !== arg.submissionID) throw new Error("No active submission");
      const isBalanceImport = submission.templateType === TemplateType.BalanceImport;

      // Explicit re-run: refetch reference lists and Static Data key sets to pick up backend changes.
      if (arg.refresh) {
        const lists = await dispatch(getReferenceData({ force: true }));
        if (getReferenceData.rejected.match(lists)) throw new Error("Reference lists could not be refreshed");
        if (isBalanceImport) {
          const keySets = await dispatch(loadExternalKeySets({ submissionID: arg.submissionID }));
          if (loadExternalKeySets.rejected.match(keySets))
            throw new Error("Static Data key sets could not be refreshed");
        }
      }

      const state = getState() as RootState;
      const uploadState = state.dataImportUpload.UploadState;
      const resolvedMapping = state.dataImportMapping.ResolvedMapping;
      const referenceData = state.dataImportTypes.ReferenceData;

      if (!uploadState || uploadState.files.length === 0) throw new Error("No uploaded files to validate");
      if (!resolvedMapping) throw new Error("Column mapping has not been confirmed");
      if (!referenceData) throw new Error("Reference lists are not loaded");

      // undefined for Static Data runs leaves external references unflagged.
      const externalKeySets = isBalanceImport ? getExternalKeySets(arg.submissionID)?.keySets : undefined;
      if (isBalanceImport && !externalKeySets) throw new Error("Static Data key sets are not loaded");

      // A previous run's raw rows (edits already applied in place) skip the file re-read. RunID > 0
      // means redux still holds that run: stepping back zeroes it, and a checkpoint has no rows.
      const previousRun = state.dataImportValidation.RunID > 0 ? getValidationRun(arg.submissionID) : undefined;
      const preReadSheets = previousRun && !previousRun.fromCheckpoint ? previousRun.rawSheets : undefined;
      const preReadWorksheetJoins = preReadSheets ? previousRun?.worksheetJoins : undefined;

      const files: ValidationWorkerFileInput[] = [];
      if (preReadSheets) {
        // Raw rows already in memory — skip file gathering.
      } else if (uploadState.mode === UploadMode.Excel) {
        const entry = uploadState.files[0];
        const file = await getOrDownloadSubmissionFile(arg.submissionID, entry.fileName, entry.submissionFileID);
        if (!file) throw new Error("File handle missing");
        const sheetSources = await resolveSheetSources(
          uploadState.sheets,
          state.dataImportUpload.SheetMappings,
          worksheetsForTemplate(submission.templateType),
          () => readWorkbookSheetNames(file)
        );
        if (sheetSources.length === 0) throw new Error(NO_WORKSHEETS_MESSAGE);
        files.push({ mode: UploadMode.Excel, file, sheetSources });
      } else {
        const entries = uploadState.files.filter((item) => item.status !== UploadFileStatus.Error);
        for (const entry of entries) {
          const file = await getOrDownloadSubmissionFile(arg.submissionID, entry.fileName, entry.submissionFileID);
          if (!file) throw new Error("File handle missing");
          files.push({
            mode: UploadMode.Csv,
            file,
            worksheet: entry.targetWorksheet ?? entry.fileName.replace(/\.csv$/i, ""),
          });
        }
      }

      // The generated enum's values are the same strings as TemplateKind — converted here so
      // the worker compilation unit never imports the generated API client.
      const templateType = toTemplateKind(submission.templateType);

      // A real file read loses inline edits (the bytes never contained them) — hand the journal
      // to the worker so it re-applies them before validating. Reused rows already carry them.
      const edits = preReadSheets
        ? undefined
        : [...getCellEdits(arg.submissionID).values()].map((edit) => ({
            worksheet: edit.worksheet,
            rowNumber: edit.rowNumber,
            field: edit.field,
            newValue: edit.newValue,
          }));

      const done = await runValidationInWorker(
        {
          templateType,
          files,
          preReadSheets,
          preReadWorksheetJoins,
          mapping: resolvedMapping.mapping,
          rowIdentifierFields: selectResolvedRowIdentifierFields(getState() as RootState),
          referenceLists: extractReferenceLists(referenceData),
          edits,
          externalKeySets,
        },
        (progress) =>
          dispatch(
            validationProgress({ phase: progress.phase, worksheet: progress.worksheet, percent: progress.percent })
          )
      );

      const runID = nextRunID++;
      setValidationRun(
        arg.submissionID,
        buildValidationRunData(runID, done.sheets, done.rawSheets, done.issues, {
          skippedRowsByWorksheet: done.skippedRowsByWorksheet,
          worksheetJoins: done.worksheetJoins,
        })
      );

      // Fire-and-forget checkpoint — showing the results shouldn't wait on the save round-trip.
      // Skipped while edits are unsaved: the checkpoint must never describe edits the server lacks.
      const editsDirty = (getState() as RootState).dataImportValidation.EditsDirty;
      if (!arg.skipCheckpoint && !editsDirty) {
        dispatch(persistValidationResult({ submissionID: arg.submissionID, summary: done.summary }) as never);
      }

      // Re-runs keep the current status.
      const nextStatus = validationStepStatus(submission.submissionStatus);
      if (nextStatus !== submission.submissionStatus) {
        dispatch(
          updateSubmissionStep({
            submissionID: arg.submissionID,
            submissionStatus: nextStatus,
            currentStep: Step.Validation,
          }) as never
        );
      }

      return { summary: done.summary, runID, validatedAt: new Date().toISOString() };
    } catch (error) {
      // The no-worksheets case has its own card in the step; the generic toast would only add noise.
      if (!(error instanceof Error && error.message === NO_WORKSHEETS_MESSAGE)) toast.error("Validation failed to run");
      throw error;
    }
  }
);

// Pushes the complete journal to SaveEdits (replace-all semantics). The edits are already
// applied and revalidated locally, so a failure only costs resume durability — the journal
// stays dirty and the user can save again.
export const saveSubmissionEdits = createAsyncThunk(
  "dataImportValidation/saveEdits",
  async (arg: { submissionID: string }) => {
    try {
      const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
      await dataImportService.saveEdits(
        arg.submissionID,
        EDITS_PAYLOAD_VERSION,
        buildEditEntries(getCellEdits(arg.submissionID))
      );
    } catch (error) {
      toast.error(writeErrorMessage(error, "Couldn't save your progress"));
      throw error;
    }
  }
);

// Hydrates the persisted edits journal (GetEdits) on resume, replacing the in-memory journal
// that died with the refresh. Runs before the first validation pass so the worker can re-apply
// the edits to the freshly-read file.
export const hydrateSubmissionEdits = createAsyncThunk(
  "dataImportValidation/hydrateEdits",
  async (arg: { submissionID: string }) => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const model = await dataImportService.getEdits(arg.submissionID);
    hydrateCellEdits(arg.submissionID, toCellEditRecords(model));
    return getCellEditCount(arg.submissionID);
  }
);

// "Leave without saving": drops the unsaved edits by restoring the server journal and
// discarding the run built on top of them, so the next visit revalidates the file afresh.
export const discardUnsavedEdits = createAsyncThunk(
  "dataImportValidation/discardEdits",
  async (arg: { submissionID: string }, { dispatch }) => {
    try {
      await dispatch(hydrateSubmissionEdits({ submissionID: arg.submissionID })).unwrap();
    } catch {
      // GetEdits failed: fall back to an empty journal rather than keeping the discarded edits.
      hydrateCellEdits(arg.submissionID, []);
    }
    clearValidationRun(arg.submissionID);
  }
);

// Serialises edit-revalidations: a run started before a later edit could finish after it and
// clobber the store with stale results. Each revalidation waits for the previous one; edits
// are already applied to the raw rows before queueing, so the newest run always sees them all.
let lastRevalidation: Promise<unknown> | null = null;

// Commits one inline cell edit locally: journals it, applies it to the cached raw rows in
// place, and revalidates over them. Nothing is sent to the server here — saveSubmissionEdits
// does that. The full engine pass is sub-second in the worker, so "incremental" revalidation
// is simply a cheap full re-run over the edited rows.
export const editCellAndRevalidate = createAsyncThunk(
  "dataImportValidation/editCell",
  async (
    arg: { submissionID: string; worksheet: string; rowNumber: number; field: string; value: string },
    { dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const run = getValidationRun(arg.submissionID);
      const mapping = state.dataImportMapping.ResolvedMapping?.mapping;
      if (!run || !mapping) throw new Error("No validation run to edit");

      const rawSheet = run.rawSheets.find((sheet) => sheet.worksheet === arg.worksheet);
      const columnKey = mapping[arg.worksheet]?.[arg.field];
      if (!rawSheet || !columnKey) throw new Error("Edited cell is not mapped");

      const join = run.worksheetJoins.find((candidate) => candidate.worksheet === arg.worksheet);
      const columnIndex = findMappedColumnIndex(rawSheet.sourceColumns, columnKey, join?.columnSources);
      const rowIndex = rawSheet.rowNumbers.indexOf(arg.rowNumber);
      if (columnIndex < 0 || rowIndex < 0) throw new Error("Edited cell not found");

      const currentValue = rawSheet.rows[rowIndex][columnIndex] ?? "";
      const newValue = arg.value.trim();
      if (newValue === currentValue) return { changed: false };

      recordCellEdit(arg.submissionID, {
        worksheet: arg.worksheet,
        rowNumber: arg.rowNumber,
        field: arg.field,
        currentValue,
        newValue,
      });
      rawSheet.rows[rowIndex][columnIndex] = newValue;

      const previous = lastRevalidation;
      const queued = async () => {
        if (previous) {
          try {
            await previous;
          } catch {
            // The previous run already surfaced its own failure.
          }
        }
        const result = await dispatch(
          runSubmissionValidation({ submissionID: arg.submissionID, skipCheckpoint: true })
        );
        if (runSubmissionValidation.rejected.match(result)) {
          // Revert the mutation and journal entry unless a later queued edit already overwrote this cell.
          if (rawSheet.rows[rowIndex][columnIndex] === newValue) {
            rawSheet.rows[rowIndex][columnIndex] = currentValue;
            recordCellEdit(arg.submissionID, {
              worksheet: arg.worksheet,
              rowNumber: arg.rowNumber,
              field: arg.field,
              currentValue: newValue,
              newValue: currentValue,
            });
          }
          throw new Error("Revalidation failed");
        }
      };
      lastRevalidation = queued();
      await lastRevalidation;
      return { changed: true };
    } catch (error) {
      toast.error("Couldn't apply your change");
      throw error;
    }
  }
);

// Re-upload of a corrected file: replaces the upload, keeps the committed
// mapping (it's keyed to header names), discards the now-stale edits journal and run data,
// and validates the new file from scratch.
export const reuploadCorrectedFile = createAsyncThunk(
  "dataImportValidation/reupload",
  // Serialisable args like uploadSubmissionFiles: the caller puts the File into
  // submissionFileStore first and passes its name here.
  async (arg: { submissionID: string; fileName: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const submission = selectActiveSubmission(state);
    const uploadState = state.dataImportUpload.UploadState;
    const file = getSubmissionFile(arg.submissionID, arg.fileName);
    if (!submission || !uploadState || !file) {
      toast.error("Couldn't upload the corrected file");
      throw new Error("No file to upload");
    }

    const extension = getFileExtension(file.name);
    const validExtension =
      uploadState.mode === UploadMode.Excel ? EXCEL_EXTENSIONS.includes(extension) : extension === "csv";
    if (!validExtension) {
      toast.error(
        uploadState.mode === UploadMode.Excel
          ? "The corrected file must be an Excel workbook (.xlsx or .xlsm)"
          : "The corrected file must be a CSV file"
      );
      throw new Error("Invalid file type");
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("The corrected file is too large (50 MB maximum)");
      throw new Error("File too large");
    }

    try {
      const entry = {
        fileName: file.name,
        fileSizeBytes: file.size,
        status: UploadFileStatus.Scanning,
        targetWorksheet: null as string | null,
        matchedByFilename: false,
      };
      if (uploadState.mode === UploadMode.Excel) {
        // sheets deliberately left undefined — the run thunk re-derives the associations from
        // the new workbook's tab names.
        dispatch(setUploadFiles({ mode: UploadMode.Excel, files: [entry] }));
      } else {
        const worksheets = worksheetsForTemplate(submission.templateType);
        const targetWorksheet = suggestWorksheet(file.name, worksheets);
        const otherFiles = uploadState.files.filter(
          (item) =>
            item.fileName !== file.name && (targetWorksheet === null || item.targetWorksheet !== targetWorksheet)
        );
        dispatch(
          setUploadFiles({
            mode: UploadMode.Csv,
            files: [...otherFiles, { ...entry, targetWorksheet, matchedByFilename: targetWorksheet !== null }],
          })
        );
      }

      const uploaded = await dispatch(
        uploadSubmissionFiles({ submissionID: arg.submissionID, fileNames: [file.name] })
      );
      if (uploadSubmissionFiles.rejected.match(uploaded)) throw new Error("Upload failed");
      dispatch(clearParsedColumnSources());

      // The journal and run data described the old file — clear both locally and server-side.
      // The server clear is fire-and-forget: a failure leaves the journal dirty, so "Save
      // progress" replaces the stale server copy wholesale.
      clearCellEdits(arg.submissionID);
      clearValidationRun(arg.submissionID);
      void dispatch(saveSubmissionEdits({ submissionID: arg.submissionID }));

      await dispatch(runSubmissionValidation({ submissionID: arg.submissionID }));
    } catch (error) {
      toast.error(writeErrorMessage(error, "Couldn't upload the corrected file"));
      throw error;
    }
  }
);

// Drops the run so the next visit validates afresh; edits and key sets are untouched.
const resetRun = (state: ValidationState) => {
  state.Status = "idle";
  state.Failure = null;
  state.Progress = null;
  state.Summary = null;
  state.RunID = 0;
  state.ValidatedAt = null;
  state.ResultPersisted = false;
};

const reset = (state: ValidationState) => {
  resetRun(state);
  state.ExternalKeySetStatus = "idle";
  state.ExternalKeySetError = null;
  state.StaticDataReference = null;
  state.EditsDirty = false;
  state.SavingEdits = false;
  state.ResultPersisted = false;
  state.PersistingResult = false;
};

export const validationSlice = createSlice({
  name: "dataImportValidation",
  initialState,
  reducers: {
    validationProgress: (state, action: PayloadAction<ValidationProgress>) => {
      state.Progress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSubmissionValidation.pending, (state) => {
        state.Status = "running";
        state.Failure = null;
        state.Progress = null;
      })
      .addCase(runSubmissionValidation.fulfilled, (state, action) => {
        state.Status = "done";
        state.Progress = null;
        state.Summary = action.payload.summary;
        state.RunID = action.payload.runID;
        state.ValidatedAt = action.payload.validatedAt;
        state.ResultPersisted = false;
      })
      .addCase(persistValidationResult.pending, (state) => {
        state.PersistingResult = true;
      })
      .addCase(persistValidationResult.fulfilled, (state, action) => {
        state.PersistingResult = false;
        state.ResultPersisted = action.payload.persisted;
      })
      .addCase(runSubmissionValidation.rejected, (state, action) => {
        state.Status = "failed";
        state.Failure = action.error.message === NO_WORKSHEETS_MESSAGE ? "noWorksheets" : "generic";
        state.Progress = null;
      })
      .addCase(loadExternalKeySets.pending, (state) => {
        state.ExternalKeySetStatus = "loading";
        state.ExternalKeySetError = null;
      })
      .addCase(loadExternalKeySets.fulfilled, (state, action) => {
        state.ExternalKeySetStatus = "done";
        state.StaticDataReference = action.payload;
      })
      .addCase(loadExternalKeySets.rejected, (state, action) => {
        state.ExternalKeySetStatus = "failed";
        state.ExternalKeySetError = action.payload ?? "failed";
      })
      .addCase(editCellAndRevalidate.fulfilled, (state, action) => {
        if (action.payload.changed) state.EditsDirty = true;
      })
      .addCase(saveSubmissionEdits.pending, (state) => {
        state.SavingEdits = true;
      })
      .addCase(saveSubmissionEdits.fulfilled, (state) => {
        state.SavingEdits = false;
        state.EditsDirty = false;
      })
      // A failed save means the server copy can't be trusted to match — even the reupload's
      // fire-and-forget clear.
      .addCase(saveSubmissionEdits.rejected, (state) => {
        state.SavingEdits = false;
        state.EditsDirty = true;
      })
      // The hydrated journal IS the server copy.
      .addCase(hydrateSubmissionEdits.fulfilled, (state) => {
        state.EditsDirty = false;
      })
      // Discarding drops the run too, so the step re-runs from the file on its next mount.
      .addCase(discardUnsavedEdits.fulfilled, (state) => {
        resetRun(state);
        state.EditsDirty = false;
      })
      // Stepping back past Validation invalidates the run: the files, sheet mappings or column mappings may change.
      .addCase(updateSubmissionStep.fulfilled, (state, action) => {
        if (action.payload.currentStep < Step.Validation) resetRun(state);
      })
      // Checkpoint prefill never downgrades a live run; RunID 0 = counts-only (no bulk data behind it).
      .addCase(hydrateValidationCheckpoint.fulfilled, (state, action) => {
        if (action.payload && !state.Summary && (state.Status === "idle" || state.Status === "running")) {
          state.Summary = action.payload.summary;
          state.RunID = action.payload.runID;
          state.ValidatedAt = action.payload.validatedAt;
          state.ResultPersisted = true;
        }
      })
      // Validation results describe the uploaded file(s): reset when the active submission
      // changes or the upload is discarded.
      .addCase(getActiveSubmission.pending, reset)
      .addCase(createSubmission.fulfilled, reset)
      .addCase(clearActiveSubmission, reset)
      .addCase(clearUpload, reset);
  },
});

export const { validationProgress } = validationSlice.actions;

export const selectValidationStatus = (state: RootState) => state.dataImportValidation.Status;
export const selectValidationFailure = (state: RootState) => state.dataImportValidation.Failure;
export const selectValidationProgress = (state: RootState) => state.dataImportValidation.Progress;
export const selectValidationSummary = (state: RootState) => state.dataImportValidation.Summary;
export const selectValidationRunID = (state: RootState) => state.dataImportValidation.RunID;
export const selectExternalKeySetStatus = (state: RootState) => state.dataImportValidation.ExternalKeySetStatus;
export const selectExternalKeySetError = (state: RootState) => state.dataImportValidation.ExternalKeySetError;
export const selectStaticDataReference = (state: RootState) => state.dataImportValidation.StaticDataReference;
export const selectEditsDirty = (state: RootState) => state.dataImportValidation.EditsDirty;
export const selectSavingEdits = (state: RootState) => state.dataImportValidation.SavingEdits;
export const selectResultPersisted = (state: RootState) => state.dataImportValidation.ResultPersisted;
export const selectPersistingResult = (state: RootState) => state.dataImportValidation.PersistingResult;
export const selectValidatedAt = (state: RootState) => state.dataImportValidation.ValidatedAt;

export default validationSlice.reducer;
